import type { PeerConnectionEvent } from 'jssip/lib/RTCSession';
import { useCallback } from 'react';
import type { CallOptions } from 'jssip/lib/UA';

import type { DeafPartyResolutionResult } from 'features/call/call-accepted/interfaces';
import { isStringAllLettersOrEmpty } from 'common/helpers/';
import { LogLevel } from 'features/analytics/enums';
import {
  formatTraceStack,
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { getCorrectUriFromList } from 'features/call/call-accepted/helpers';
import { ConnectionChangeType } from 'features/call/call-base/enums';
import { removePlusFromPhoneNumber } from 'features/call/call-base/helpers';
import { CallDaoService } from 'features/call/call-base/services';
import {
  deafPartyResolved,
  setDeafIsLoading,
} from 'features/call/call-deaf/store';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import {
  setCallWasPlaced,
  setShouldCreateDataChannel,
} from 'features/call/vrs-call/store/';
import {
  dialedNumbersListSelector,
  setDialedNumberHistory,
} from 'features/dialed-numbers-history/store';
import { envConfig } from 'features/environment/services';
import { useMonitorCustomHeader } from 'features/monitoring/hooks/useMonitorCustomHeader';
import { isMonitorHostIpExistsSelector } from 'features/monitoring/store/selector';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { useConnectionRefs } from 'features/router/hooks';
import { sipSettings } from 'features/sip/config';
import { SipSessionEventBus } from 'features/sip/services';
import { isConvo, isSorenson } from 'features/vrs-third-parties/helpers';
import { ANONYMOUS } from 'features/voice-meeting/constants';
import { useSipHeaders } from 'features/sip/hooks';

export const useCallDeafParticipantHandlers = () => {
  const dispatch = useAppDispatch();
  const { uaRef } = useConnectionRefs();
  const sipHeaders = useSipHeaders({ isRemoteMercuryCall: false });
  const { monitorCustomHeader } = useMonitorCustomHeader();
  const dialedNumbersList = useAppSelector(dialedNumbersListSelector);
  const isCallMonitoring = useAppSelector(isMonitorHostIpExistsSelector);
  const isVrsMode = useAppSelector(isVrsModeSelector);
  const hearingPhoneNumber = useAppSelector(primaryHearingPhoneNumberSelector);

  const resolvePhoneNumberWithCore = useCallback(
    async (
      callerNumber: string,
      calleeNumber: string
    ): Promise<DeafPartyResolutionResult> => {
      try {
        const res = await CallDaoService.resolvePhoneNumberWithCore(
          callerNumber,
          calleeNumber
        );
        const uri = getCorrectUriFromList(res?.URIList);

        return {
          uri,
          isConvo: isConvo(res?.URIList),
          isSorenson: isSorenson(res?.URIList),
          sipRemoteDomain: uri.split('@')[1] ?? 'unresolved.sorenson.com',
          name: res?.Name ?? '',
          videoMsgData: res?.VideoMsgData?.NumberRings
            ? {
                numberRings: res.VideoMsgData.NumberRings,
                greetingPreference: res.VideoMsgData.GreetingPreference,
              }
            : undefined,
        };
      } catch (e: any) {
        sendAnalyticsError({
          Method: 'resolvePhoneNumberWithCore',
          Level: LogLevel.ERROR,
          Message: e?.message || 'error resolving phone number with core',
        });
        return {
          uri: '',
          isConvo: false,
          isSorenson: false,
          sipRemoteDomain: 'unresolved.sorenson.com',
          name: '',
        };
      }
    },
    []
  );

  const handleDeafCancel = useCallback(() => {
    // TODO: Mock this for the new test
    if (!uaRef?.current) {
      console.warn('DEBUG: jssip.UA not found');
      return;
    }

    dispatch(setDeafIsLoading(true));

    uaRef.current.terminateSessions({
      status_code: 200,
    });
    ParticipantsStatusEventBus.deaf.$disconnected.next({
      connectionChangeType: ConnectionChangeType.AGENT_DISCONNECT_DEAF,
      isConnectionCallStateNeeded: false,
    });
  }, [uaRef, dispatch]);

  const handleDeafConnect = useCallback(
    async (phoneNumber: string, calleeConnectionString?: string) => {
      let sipRemoteDomain: string;
      const phoneNumberWithoutPlus = phoneNumber.replace(/^\+/, '');
      if (dialedNumbersList.includes(phoneNumberWithoutPlus)) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'multipleDeafConnect',
            Message:
              'Attempted to dial the same deaf participant number multiple times',
            PhoneNumber: phoneNumber,
          })
        );
      }
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleDeafConnect',
          Message: 'Initiating connection to deaf participant',
          PhoneNumber: phoneNumber,
          Trace: formatTraceStack(
            new Error('stack trace').stack ?? 'no stack trace'
          ),
        })
      );
      if (!uaRef?.current) {
        console.warn('DEBUG: jssip.UA not found');
        return;
      }
      dispatch(
        setDialedNumberHistory([phoneNumberWithoutPlus, ...dialedNumbersList])
      );
      dispatch(setCallWasPlaced());
      dispatch(setDeafIsLoading(true));

      const settings = sipSettings.get();

      const fromUserName = hearingPhoneNumber || calleeConnectionString;

      let hearingNumberToResolveWithCore = hearingPhoneNumber;
      const isStringAllLettersResult =
        isStringAllLettersOrEmpty(hearingPhoneNumber);
      if (isStringAllLettersResult) {
        hearingNumberToResolveWithCore = ANONYMOUS;
      }

      let extraHeaders = sipHeaders;

      // Adding monitoring host IP address for the monitored agent
      if (isCallMonitoring && extraHeaders?.length) {
        extraHeaders = extraHeaders.concat(monitorCustomHeader);
      }

      const options: CallOptions = {
        extraHeaders,
        pcConfig: settings.pcConfig,
        mediaConstraints: {
          audio: true,
          video: true,
        },
        eventHandlers: {
          peerconnection: (event: PeerConnectionEvent) => {
            SipSessionEventBus.peerConnection$.next(event);
          },
        },
        ...(fromUserName ? { fromUserName } : {}),
      };
      if (isVrsMode) {
        const resolveInfo = await resolvePhoneNumberWithCore(
          removePlusFromPhoneNumber(hearingNumberToResolveWithCore),
          removePlusFromPhoneNumber(phoneNumber)
        );
        if (resolveInfo) {
          dispatch(deafPartyResolved(resolveInfo));
        }
        sipRemoteDomain = resolveInfo.sipRemoteDomain;
        if (resolveInfo.isConvo) {
          console.log('DEBUG: Changing behavior for Convo. No datachannel.', {
            resolveInfo,
          });
          delete options.eventHandlers;
          dispatch(setShouldCreateDataChannel(false));
        } else {
          dispatch(setShouldCreateDataChannel(true));
        }
      } else {
        sipRemoteDomain = envConfig.vri.sipCustomerEndpoint;
      }

      const callee = `sip:+${phoneNumberWithoutPlus}@${sipRemoteDomain}`.trim();

      try {
        uaRef.current.call(callee, options);
        ParticipantsStatusEventBus.deaf.$connecting.next();
      } catch (e: any) {
        sendAnalyticsError({
          Method: 'handleDeafConnect',
          Level: LogLevel.ERROR,
          Message: e?.message || 'error connecting to deaf party',
        });
        handleDeafCancel();
      }
    },

    [
      dialedNumbersList,
      dispatch,
      uaRef,
      hearingPhoneNumber,
      sipHeaders,
      isCallMonitoring,
      isVrsMode,
      monitorCustomHeader,
      resolvePhoneNumberWithCore,
      handleDeafCancel,
    ]
  );

  const handleDeafDisconnect = () => {
    dispatch(setDeafIsLoading(true));
    SipSessionEventBus.terminate$.next();
  };

  return {
    handleDeafConnect,
    handleDeafCancel,
    handleDeafDisconnect,
  };
};
